import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { Card, KPI, Row } from "./_shared";

export function EconomicsTab() {
  const { apiFetch } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showAssumptions, setShowAssumptions] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try { const d = await apiFetch("/api/admin/economics"); setData(d); }
    catch (e) { setErr(e?.message || "Failed to load"); }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-sm text-[#EF4444]">Error: {err}</div>;
  if (!data) return <div className="text-xs text-[var(--srs-ink-faint)]">Loading economics…</div>;

  const u = data.users, a = data.activity, p = data.price, vc = data.variable_cost_per_user;
  const ue = data.unit_economics, ms = data.monthly_snapshot, asu = data.assumptions;
  const fmtNok = (n) => `${Number(n).toLocaleString("nb-NO", { maximumFractionDigits: 2 })} kr`;
  const fmt = (n) => Number(n).toLocaleString("nb-NO");

  // Forward projections at the current margin (for "what if" planning)
  const projections = [100, 500, 1000, 5000, 10000].map((n) => ({
    users: n,
    gross: n * p.gross_per_user,
    net:   n * ue.net_per_paying_user_per_month,
    yearly_net: n * ue.net_per_paying_user_per_month * 12,
  }));

  return (
    <div className="space-y-6" data-testid="economics-root">
      <Card title="UNIT ECONOMICS · LIVE"
            subtitle={`Snapshot as of ${new Date(data.as_of).toLocaleString()}. Numbers come from Mongo; assumptions are in this dashboard's footer.`}
            accent="#10B981" testid="economics-card">

        {/* Headline KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPI label="PAYING USERS" value={fmt(u.paying)} accent="#10B981" testid="kpi-paying" />
          <KPI label="TRIALING" value={fmt(u.trialing)} accent="#FF8A5C" testid="kpi-trialing" />
          <KPI label="GROSS MRR" value={fmtNok(ms.gross_mrr)} accent="#0B6CF5" testid="kpi-gross-mrr" />
          <KPI label="NET MRR" value={fmtNok(ms.net_mrr)} accent="#0B2440" testid="kpi-net-mrr" />
        </div>

        {/* Per-user breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 rounded-lg" data-testid="per-user-card">
            <div className="text-[10px] tracking-[0.28em] font-bold uppercase text-[var(--srs-ink-dim)] mb-3">PER PAYING USER · MONTH</div>
            <Row label="Gross"             value={fmtNok(p.gross_per_user)} />
            <Row label="VAT (25% incl.)"   value={`–${fmtNok(p.vat_per_user)}`} />
            <Row label="Stripe fee"        value={`–${fmtNok(p.stripe_per_user)}`} />
            <div className="my-2 h-px bg-[var(--srs-line)]" />
            <Row label="Net after VAT + Stripe" value={fmtNok(p.net_after_vat_stripe)} bold />
            <Row label="– AI scans"        value={`–${fmtNok(vc.ai_scans)}`} />
            <Row label="– Email (Resend)"  value={`–${fmtNok(vc.email)}`} />
            <Row label="– Database"        value={`–${fmtNok(vc.database)}`} />
            <div className="my-2 h-px bg-[var(--srs-line)]" />
            <Row label="NET / USER / MONTH" value={fmtNok(ue.net_per_paying_user_per_month)} bold accent="#10B981" />
            <Row label="Gross margin"      value={`${ue.gross_margin_pct}%`} />
          </div>

          <div className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 rounded-lg" data-testid="activity-card">
            <div className="text-[10px] tracking-[0.28em] font-bold uppercase text-[var(--srs-ink-dim)] mb-3">ACTIVITY · LAST 30 DAYS</div>
            <Row label="Total users"           value={fmt(u.total)} />
            <Row label="Canceled / lapsed"     value={fmt(u.canceled)} />
            <Row label="Signups (30d)"         value={fmt(u.signups_last_30d)} />
            <Row label="Signups (365d)"        value={fmt(u.signups_last_365d)} />
            <div className="my-2 h-px bg-[var(--srs-line)]" />
            <Row label="Cached domains"        value={fmt(a.cached_domains)} />
            <Row label="Est. AI scans (30d)"   value={fmt(a.estimated_ai_scans_last_30d)} />
            <Row label="Scans / paying user"   value={fmt(a.scans_per_paying_user_per_month)} />
            <div className="my-2 h-px bg-[var(--srs-line)]" />
            <Row label="Open support tickets"  value={fmt(a.support_tickets_open)} />
            <Row label="Tickets opened (30d)"  value={fmt(a.support_tickets_last_30d)} />
          </div>
        </div>

        {/* Projections */}
        <div className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 rounded-lg mb-6" data-testid="projections-card">
          <div className="text-[10px] tracking-[0.28em] font-bold uppercase text-[var(--srs-ink-dim)] mb-3">PROJECTIONS · AT CURRENT MARGIN</div>
          <div className="text-[11px] text-[var(--srs-ink-faint)] mb-3">
            What you'd net if you held the current ~{ue.gross_margin_pct}% margin and scaled the paying base.
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.18em] text-[var(--srs-ink-dim)]">
                <th className="text-left py-2">Paying users</th>
                <th className="text-right py-2">Gross / mo</th>
                <th className="text-right py-2">Net / mo</th>
                <th className="text-right py-2">Net / yr</th>
              </tr>
            </thead>
            <tbody>
              {projections.map((row) => (
                <tr key={row.users} className="border-t border-[var(--srs-line-soft)]" data-testid={`projection-${row.users}`}>
                  <td className="py-2 font-mono text-[var(--srs-ink)]">{fmt(row.users)}</td>
                  <td className="py-2 text-right text-[var(--srs-ink-dim)]">{fmtNok(row.gross)}</td>
                  <td className="py-2 text-right text-[var(--srs-ink)]">{fmtNok(row.net)}</td>
                  <td className="py-2 text-right font-bold" style={{ color: "#10B981" }}>{fmtNok(row.yearly_net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Annual run-rate */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <KPI label="YEARLY GROSS RUN-RATE" value={fmtNok(ms.yearly_run_rate_gross)} accent="#0B6CF5" testid="kpi-yearly-gross" big />
          <KPI label="YEARLY NET RUN-RATE"   value={fmtNok(ms.yearly_run_rate_net)} accent="#10B981" testid="kpi-yearly-net" big />
        </div>

        {/* Assumptions toggle */}
        <button onClick={() => setShowAssumptions((v) => !v)}
                data-testid="toggle-assumptions"
                className="text-[10px] tracking-[0.25em] uppercase font-bold text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] mb-2">
          {showAssumptions ? "↑ Hide assumptions" : "↓ Show assumptions"}
        </button>
        {showAssumptions && (
          <div className="border border-dashed border-[var(--srs-line)] p-3 rounded text-[11px] text-[var(--srs-ink-dim)] space-y-1" data-testid="assumptions-card">
            <div>Scans per cached domain / month: <span className="text-[var(--srs-ink)] font-mono">{asu.scans_per_cached_domain_per_month}</span></div>
            <div>AI scan cost (NOK): <span className="text-[var(--srs-ink)] font-mono">{asu.scan_cost_nok}</span></div>
            <div>Stripe fee: <span className="text-[var(--srs-ink)] font-mono">{(asu.stripe_pct * 100).toFixed(1)}% + {asu.stripe_fixed_nok} NOK</span></div>
            <div>VAT (MVA): <span className="text-[var(--srs-ink)] font-mono">{(asu.vat_rate * 100).toFixed(0)}%</span></div>
            <div>Email cost (Resend) / user / mo (NOK): <span className="text-[var(--srs-ink)] font-mono">{asu.email_cost_nok_per_user}</span></div>
            <div>DB hosting / user / mo (NOK): <span className="text-[var(--srs-ink)] font-mono">{asu.db_hosting_nok_per_user}</span></div>
            <div className="pt-2 italic text-[var(--srs-ink-faint)]">
              Tunable in <span className="font-mono">server.py · admin_economics()</span>. Adjust as you get real cost data.
            </div>
          </div>
        )}

        <button onClick={load} data-testid="economics-refresh"
                className="ml-2 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-bold border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)]">
          Refresh
        </button>
        <MigrateSubscribersButton />
      </Card>
    </div>
  );
}

export function MigrateSubscribersButton() {
  const { apiFetch } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const migrate = async () => {
    if (!window.confirm("Migrer alle aktive abonnement til gjeldende månedspris (29 NOK). Eksisterende kunder vil bli oppdatert ved neste fakturasyklus uten umiddelbar proratering. Fortsette?")) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const d = await apiFetch("/api/admin/migrate-subscribers-to-current-price", { method: "POST" });
      setResult(d);
    } catch (e) { setErr(e?.message || "Migration failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border border-dashed border-[var(--srs-line)] p-4 rounded-lg" data-testid="migrate-block">
      <div className="text-[10px] tracking-[0.28em] font-bold uppercase text-[var(--srs-ink-dim)] mb-2">PRICE MIGRATION</div>
      <p className="text-[11px] text-[var(--srs-ink-faint)] mb-3">
        One-shot: pushes every active subscription onto the current monthly Stripe price.
        Existing customers keep their old price until you hit this. No proration.
      </p>
      <button onClick={migrate} disabled={busy} data-testid="migrate-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981] text-white text-xs font-bold tracking-[0.18em] uppercase rounded-full disabled:opacity-50">
        {busy ? "Migrating…" : "Migrate all subscribers to current price"}
      </button>
      {err && <div className="text-xs text-[#EF4444] mt-2" data-testid="migrate-err">{err}</div>}
      {result && (
        <div className="text-[11px] text-[var(--srs-ink)] mt-3 font-mono" data-testid="migrate-result">
          ✓ Migrated <strong>{result.migrated_count}</strong> · Skipped {result.skipped_count} · Errors {result.error_count}
          {result.migrated_count > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[var(--srs-ink-dim)]">Show details</summary>
              <pre className="text-[10px] mt-2 overflow-auto max-h-40 p-2 bg-[var(--srs-surface-2)] rounded">{JSON.stringify(result, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deals Tab — admin creates timed promotional discounts. Creates a matching
// Stripe Coupon on save so checkouts auto-apply the discount.
// ---------------------------------------------------------------------------
